import { isCvImportReviewCancelled, isCvImportSourceChanged } from '../../lib/cv-import-review';
import { persistCvResumeReplacement } from "../../lib/cv-resume-replacement";
import { NativeButton } from "../common/NativeButton";
import {
  fetchTailoredCV,
  replaceTailoredCvFromParsed,
} from "../../lib/tailored-cv-api";
import { buildTailoredCvRoute } from "../../lib/cv-target-route";
import { invalidateCvDocumentQueries } from "../../lib/cv-query-invalidation";
import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "expo-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { useToast } from "../../hooks/useToast";
import {
  CV_REPLACEMENT_INCOMPLETE_CODE,
  getOnboardingValidationMessage,
  isCvReplacementIncompleteError,
  saveSections,
} from "../../lib/onboarding-api";
import { useCVData } from "../../lib/cv-mock-data";
import {
  buildCvProfileReplacementFromParsed,
  isImportedCvIncompleteError,
} from "../../lib/cv-save-sections";
import { confirm } from "../../lib/alert.web";
import { WebEditPageShell } from "./WebEditPageShell";

import {
  ResumeImportComposer,
  type ResumeImportComposerParsed,
} from "./ResumeImportComposer";
import type { ParsedCvData } from "@tabbio/types";
import { trackProductEvent } from "../../lib/product-analytics";

export function CVResumeImportScreen({
  tailoredCvId,
}: { tailoredCvId?: string } = {}) {
  const { t } = useTranslation("seeker");
  const router = useRouter();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { updateProfile, target } = useCVData();
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const revision = useRef<string | null>(null);
  const cvQuery = useQuery({
    queryKey: ["cv", tailoredCvId, "import-revision"],
    queryFn: () => fetchTailoredCV(tailoredCvId!),
    enabled: Boolean(tailoredCvId),
    staleTime: 0,
    refetchOnMount: "always",
  });
  useEffect(() => {
    if (cvQuery.data && !cvQuery.isFetching && !revision.current)
      revision.current = cvQuery.data.updatedAt;
  }, [cvQuery.data, cvQuery.isFetching]);
  const cvName = cvQuery.data?.title || t("cv.resumeImport.thisCv");
  // A tailored CV has to load before its import can replace it.
  const cvLoading = Boolean(tailoredCvId && (!cvQuery.data || cvQuery.isFetching));
  const cvFailed = Boolean(tailoredCvId && cvQuery.isError);

  const doSave = useCallback(
    async (parsed: ParsedCvData, importEvidenceId?: string) => {
      if (savingRef.current) return;
      savingRef.current = true;
      setIsSaving(true);
      try {
        await persistCvResumeReplacement(
          { parsed, importEvidenceId, tailoredCvId, target, expectedUpdatedAt: revision.current },
          {
            replaceTailored: replaceTailoredCvFromParsed,
            saveProfile: saveSections,
          },
        );
        if (tailoredCvId) {
          await invalidateCvDocumentQueries(queryClient, tailoredCvId);
        } else {
          const profileReplacement =
            buildCvProfileReplacementFromParsed(parsed);
          updateProfile(profileReplacement);
          queryClient.setQueryData(
            ["profile", target || "self"],
            (previous: unknown) => ({
              ...(previous && typeof previous === "object" ? previous : {}),
              ...profileReplacement,
            }),
          );
          await invalidateCvDocumentQueries(queryClient);
          await queryClient.invalidateQueries({
            queryKey: ["onboarding", "resume-source"],
          });
        }
        trackProductEvent("cv_imported", {
          source: "resume_import",
          target: tailoredCvId ? "tailored" : "profile",
        });
        toast.success(
          t("cv.resumeImport.successTitle"),
          t(
            tailoredCvId
              ? "cv.resumeImport.tailoredSuccessMessage"
              : "cv.resumeImport.successMessage",
          ),
        );
        router.replace(
          (tailoredCvId
            ? buildTailoredCvRoute(tailoredCvId)
            : "/cv?resumeImport=updated") as any,
        );
      } catch (error) {
        if (isCvImportReviewCancelled(error)) return;
        // The server (or the client guard) refused a replacement that would
        // have emptied the current CV: say so, the CV is unchanged.
        const incomplete =
          isCvReplacementIncompleteError(error) ||
          isImportedCvIncompleteError(error) ||
          // Tailored transport preserves envelope codes on a plain Error.
          (typeof error === "object" &&
            error !== null &&
            "code" in error &&
            error.code === CV_REPLACEMENT_INCOMPLETE_CODE);
        toast.error(
          t("cv.resumeImport.failedTitle"),
          isCvImportSourceChanged(error)
            ? t("cv.importEvidence.sourceChanged")
            : incomplete
            ? t("cv.resumeImport.replacementIncomplete")
            : error &&
                typeof error === "object" &&
                "code" in error &&
                error.code === "CONFLICT"
              ? t("cv.resumeImport.conflict")
              : (getOnboardingValidationMessage(error) ??
                t("cv.resumeImport.failedMessage")),
        );
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [queryClient, router, t, target, toast, updateProfile, tailoredCvId],
  );

  const handleParsed = useCallback(
    async ({ parsed, quality, importEvidenceId }: ResumeImportComposerParsed) => {
      // Exact extraction (no AI) is called out before the user confirms.
      const baseConfirmMessage = t(
        tailoredCvId
          ? "cv.resumeImport.tailoredConfirmMessage"
          : "cv.resumeImport.confirmMessage",
        { name: cvName },
      );
      const confirmMessage = quality?.fallbackUsed
        ? `${baseConfirmMessage} ${t("cv.resumeImport.exactExtractionNote")}`
        : baseConfirmMessage;
      const confirmed = await confirm({
        title: t(
          tailoredCvId
            ? "cv.resumeImport.tailoredConfirmTitle"
            : "cv.resumeImport.confirmTitle",
          { name: cvName },
        ),
        message: confirmMessage,
        confirmLabel: t("cv.resumeImport.replace"),
        destructive: true,
      });
      if (!confirmed) return;
      await doSave(parsed, importEvidenceId);
    },
    [doSave, t, tailoredCvId, cvName],
  );

  return (
    <WebEditPageShell title={t("cv.resumeImport.title")}>
      <div style={{ maxWidth: 560, marginInline: "auto", width: "100%" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "fit-content",
            maxWidth: "100%",
            margin: "0 auto 22px",
            padding: "8px 12px",
            borderRadius: 999,
            border: "1px solid var(--input-border)",
            backgroundColor: "transparent",
          }}
        >
          <div
            aria-hidden
            style={{
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 auto",
              color: "var(--text-secondary)",
            }}
          >
            <AlertTriangle size={17} strokeWidth={1.9} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 14,
              lineHeight: "18px",
              color: "var(--text-secondary)",
            }}
          >
            {t(
              tailoredCvId
                ? "cv.resumeImport.tailoredReplaceWarning"
                : "cv.resumeImport.replaceWarning",
              { name: cvName },
            )}
          </p>
        </div>

        {cvFailed ? (
          <div
            role="alert"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              margin: "0 0 16px",
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: "20px",
                color: "var(--text-secondary)",
                textAlign: "center",
              }}
            >
              {t("cv.resumeImport.loadFailed")}
            </p>
            <NativeButton
              title={t("common:actions.retry")}
              onPress={() => {
                void cvQuery.refetch();
              }}
              variant="outline"
            />
          </div>
        ) : null}
        {/* Parse lands straight on the replace confirmation: the raw
            field-by-field dump this used to render between them was
            unreadable next to the styled CV it was about to become. */}
        <ResumeImportComposer
          title={t("cv.resumeImport.pageTitle")}
          subtitle={t("cv.resumeImport.pageSubtitle")}
          placeholder={t("cv.resumeImport.placeholder")}
          parseFailedTitle={t("cv.resumeImport.failedTitle")}
          parseFailedMessage={t("cv.resumeImport.failedMessage")}
          onParsed={handleParsed}
          secondaryActionLabel={t("cv.resumeImport.cancel")}
          onSecondaryAction={() => router.back()}
          // Only the save keeps the wait up; loading this CV only locks the
          // input, because nothing is being read yet.
          disabled={isSaving}
          inputLocked={cvLoading || cvFailed}
          skipEnrichment
        />
      </div>
    </WebEditPageShell>
  );
}
